import type { SmokeStatus, SmokeSummary } from '../types/smoke.js';

interface SmokeStatusBadgeProps {
  status: SmokeStatus;
  summary?: Pick<SmokeSummary, 'routeCount' | 'failureCount' | 'elapsedMs'>;
  className?: string;
}

function buildTitle(status: SmokeStatus, summary?: SmokeStatusBadgeProps['summary']): string {
  const routeCount = summary?.routeCount;
  const failureCount = summary?.failureCount;
  const elapsedMs = summary?.elapsedMs;
  const elapsedSec = elapsedMs != null ? Math.round(elapsedMs / 1000) : null;

  switch (status) {
    case 'passed': {
      let parts = 'Smoke test passed';
      if (routeCount != null) parts += ` — ${routeCount} route${routeCount === 1 ? '' : 's'}`;
      if (elapsedSec != null) parts += `${routeCount != null ? ',' : ' —'} ${elapsedSec}s`;
      return parts;
    }
    case 'failed': {
      let parts = 'Smoke test failed';
      if (failureCount != null) parts += ` — ${failureCount} failure${failureCount === 1 ? '' : 's'}`;
      return parts;
    }
    case 'deferred':
      return 'Smoke test deferred — harness/config error';
    case 'not_run':
    default:
      return 'No smoke verdict recorded for this ticket';
  }
}

const CONFIG: Record<
  SmokeStatus,
  { classes: string; glyph: string; label: string }
> = {
  passed: {
    classes: 'bg-green-500 text-white border-green-500',
    glyph: '✓',
    label: 'smoke',
  },
  failed: {
    classes: 'bg-red-500 text-white border-red-500',
    glyph: '✕',
    label: 'smoke',
  },
  deferred: {
    classes: 'bg-amber-500 text-white border-amber-500',
    glyph: '!',
    label: 'smoke',
  },
  not_run: {
    classes: 'bg-transparent text-gray-500 border-gray-300 border-dashed',
    glyph: '−',
    label: 'not smoked',
  },
};

export function SmokeStatusBadge({ status, summary, className = '' }: SmokeStatusBadgeProps) {
  const config = CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${config.classes} ${className}`}
      title={buildTitle(status, summary)}
    >
      <span aria-hidden="true">{config.glyph}</span>
      <span>{config.label}</span>
    </span>
  );
}
